import { format } from "date-fns";

import type { DealStage } from "../types";

export const findDealLabel = (dealStages: DealStage[], dealValue: string) => {
  const dealStage = dealStages.find((stage) => stage.value === dealValue);
  return dealStage?.label;
};

/**
 * Stages that always close a deal, whatever the configuration says.
 *
 * The `companies_summary` SQL view hardcodes `stage not in ('won', 'lost')` —
 * it cannot read the runtime configuration — so these two are the floor every
 * surface shares, and the accounts table's Pipeline column is computed with
 * exactly this rule.
 */
export const CLOSED_DEAL_STAGES = ["won", "lost"];

/**
 * The stages a deal is still in play in: everything that is neither a hardcoded
 * closed outcome nor a stage the administrator listed in `dealPipelineStatuses`.
 *
 * Defined once so every "open pipeline" figure on screen adds up the same
 * deals. On the shipped configuration (`dealPipelineStatuses = ["won"]`) this is
 * exactly the SQL view's set; configuring extra pipeline statuses narrows the
 * dashboard further than the view, which has no way to know about them.
 */
export const getOpenDealStages = (
  dealStages: DealStage[],
  dealPipelineStatuses: string[],
): DealStage[] =>
  dealStages.filter(
    (stage) =>
      !CLOSED_DEAL_STAGES.includes(stage.value) &&
      !dealPipelineStatuses.includes(stage.value),
  );

/**
 * How much of an open deal's amount counts as expected revenue, by stage.
 *
 * A weight is a judgement call the configuration does not carry — `dealStages`
 * holds only a value and a label — so these defaults are keyed by the stage
 * values shipped in `defaultConfiguration`.
 */
export const DEFAULT_STAGE_WEIGHTS: Record<string, number> = {
  opportunity: 0.2,
  "proposal-sent": 0.5,
  "in-negociation": 0.8,
  delayed: 0.3,
};

/**
 * A stage with no default weight — a custom one, or a renamed default — weighs
 * 0. Returning `undefined` here would multiply an amount by `undefined` and
 * turn a whole revenue bar into NaN.
 */
export const getStageWeight = (stage: string): number =>
  DEFAULT_STAGE_WEIGHTS[stage] ?? 0;

export function getRelativeTimeString(
  dateString: string,
  locale = "en",
): string {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = date.getTime() - today.getTime();
  const unitDiff = Math.round(diff / (1000 * 60 * 60 * 24));

  // Check if the date is more than one week old
  if (Math.abs(unitDiff) > 7) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
    }).format(date);
  }

  // Intl.RelativeTimeFormat for dates within the last week
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return ucFirst(rtf.format(unitDiff, "day"));
}

function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

export function formatISODateString(dateString: string) {
  if (!isoDateStringRegex.test(dateString)) {
    throw new Error("Invalid date format. Expected YYYY-MM-DD.");
  }
  // Some browsers will consider a date in the format YYYY-MM-DD as UTC, which can cause off-by-one-day issues depending on the user's timezone.
  // To avoid this, we can parse the date components manually and create a date object in the local timezone.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return format(date, "PP");
}
